/**
 * Cross-entity aggregator: combines `listCustomWorkflows`
 * (entities/custom-workflow) with `useCurrentWorkspaceId`
 * (entities/workspace) to produce the workspace-scoped custom-workflows list.
 * FSD prohibits cross-entity value imports between sibling entity slices,
 * so this aggregator MUST live above the entity layer.
 *
 * Putting it in `widgets/` would block features from consuming it (features →
 * widgets is also banned), so `shared/` is the pragmatic home. The
 * `shared/hooks/useCustomWorkflows/` directory is classified under the
 * `shared-cross-entity-aggregator` element type in `eslint.config.js`, which
 * grants it (and ONLY it) `shared → entities` value imports.
 *
 * Mirrors the pattern established by `useWorkspaceAuth`,
 * `useKnowledgeBasesList`, and `useFoldersWithGuidelines`.
 *
 * Lifted from `features/custom-workflows/model/useCustomWorkflows/useCustomWorkflows.ts`
 * (re-exported from old path for backward compat).
 */
import { useQuery } from '@tanstack/react-query';
import { ListCustomWorkflowsResponseDto } from '@inference.engine/api-client/web';

import { listCustomWorkflows } from '@/entities/custom-workflow';
import { useCurrentWorkspaceId } from '@/entities/workspace';

export const useCustomWorkflows = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const selectedWorkspaceId = useCurrentWorkspaceId();
  const { data, isLoading } = useQuery<ListCustomWorkflowsResponseDto>({
    enabled: enabled && !!selectedWorkspaceId,
    queryKey: ['custom-workflows', selectedWorkspaceId],
    queryFn: () => listCustomWorkflows(selectedWorkspaceId!),
  });

  const customWorkflows = data?.data ?? [];

  return { customWorkflows, isLoading };
};
